import { Users, Mail, Handshake, TrendingUp, Workflow, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const steps = [
  {
    icon: Users,
    number: "01",
    title: "Prospecting",
    subtitle: "Targets",
    description: "Who do we target?",
    points: [
      "Discovering high quality link opportunities in your niche",
      "Finding the right decision makers and editors",
      "Collecting personalized data for each prospect",
      "Vetting sites for genuine organic traffic and authority",
    ],
    color: "from-blue-500 to-cyan-500",
    bgColor: "bg-blue-500/10",
  },
  {
    icon: Mail,
    number: "02",
    title: "Outreach",
    subtitle: "Strategy",
    description: "Launching campaigns",
    points: [
      "Crafting compelling, personalized email templates",
      "Running multiple outreach campaigns simultaneously",
      "A/B testing subject lines and messaging",
      "Scheduling strategic follow ups and calls",
    ],
    color: "from-purple-500 to-pink-500",
    bgColor: "bg-purple-500/10",
  },
  {
    icon: Handshake,
    number: "03",
    title: "Relationship",
    subtitle: "Building",
    description: "Connecting with publishers",
    points: [
      "Building trust with editors and site owners",
      "Nurturing long term publisher relationships",
      "Providing genuine value to link partners",
      "Earning links that truly impact rankings",
    ],
    color: "from-emerald-500 to-green-500",
    bgColor: "bg-emerald-500/10",
  },
  {
    icon: TrendingUp,
    number: "04",
    title: "Deliver",
    subtitle: "Results",
    description: "Tracking and optimization",
    points: [
      "Continuous monitoring of link metrics and performance",
      "Regular benchmarking against competitors",
      "Transparent reporting with actionable insights",
      "Strategic planning to stay ahead of algorithm updates",
    ],
    color: "from-accent to-orange-500",
    bgColor: "bg-accent/10",
  },
];

const HowWeWork = () => {
  return (
    <section className="py-20 md:py-32 bg-muted/30 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-20 right-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-72 h-72 bg-accent/5 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Workflow className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Our Process</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight">
            Here's How We
            <span className="block mt-2 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent pb-2" style={{ lineHeight: '1.2' }}>
              Make It Happen
            </span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Every client gets a custom game plan. Our SEO team carefully maps out the exact types of links that'll work best for your business.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-7xl mx-auto">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className="group relative bg-card rounded-3xl p-6 md:p-8 border border-border/50 hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 overflow-hidden"
            >
              {/* Background gradient on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
              
              {/* Large number background */}
              <div className="absolute -top-4 -right-4 text-8xl font-black text-primary/5 group-hover:text-primary/10 transition-colors">
                {step.number}
              </div>
              
              <div className="relative">
                <div className={`w-14 h-14 md:w-16 md:h-16 ${step.bgColor} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <step.icon className="w-7 h-7 md:w-8 md:h-8 text-primary" />
                </div>
                
                <div className="mb-4">
                  <h3 className="text-xl md:text-2xl font-bold text-foreground inline">
                    {step.title}
                  </h3>
                  <span className={`text-xl md:text-2xl font-bold ml-2 bg-gradient-to-r ${step.color} bg-clip-text text-transparent pb-1`} style={{ lineHeight: '1.2' }}>
                    {step.subtitle}
                  </span>
                </div>
                
                <p className="text-xs md:text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                  {step.description}
                </p>
                
                <ul className="space-y-3">
                  {step.points.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-muted-foreground">
                      <div className={`w-2 h-2 bg-gradient-to-r ${step.color} rounded-full mt-1.5 flex-shrink-0`} />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 md:mt-20 max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-r from-primary via-[hsl(var(--hero-gradient-end))] to-primary rounded-3xl p-8 md:p-12 text-center overflow-hidden">
            {/* Animated background */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 left-1/4 w-32 h-32 bg-white rounded-full blur-2xl animate-pulse" />
              <div className="absolute bottom-0 right-1/4 w-40 h-40 bg-accent rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }} />
            </div>
            
            <div className="relative z-10">
              <p className="text-xl md:text-2xl text-white font-semibold mb-3">
                The bottom line?
              </p>
              <p className="text-white/80 text-lg mb-6">
                Every campaign we run is built specifically around your goals. We don't do one-size-fits-all. Each strategy is carefully planned, executed, and fine-tuned to get you results that stick around.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button variant="secondary" className="px-6 py-3 h-auto text-base">
                    Start Your Campaign <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/b2b-link-building">
                  <Button variant="outline" className="px-6 py-3 h-auto text-base border-white/30 text-white hover:bg-white/10">
                    B2B Link Building <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowWeWork;
